// ANIME LAYER prototype (owner 2026-11-02: "a little bit more of an anime-ish layer" on top of the cel look). Behind a flag:
// ?look=anime turns it on; without the flag none of these nodes is built (default rendering is unchanged).
// ?lookcheck builds the nodes too, so before/after captures of the SAME frozen frame flip it at runtime (ANIME.set).
//
// Art direction (P1, 2026-11-02) — visual hierarchy: Aren and the major threats look deliberately illustrated, the horde
// stays simple. Priorities: (1) selective ink + controlled rim, (2) 2–3 band cel with COLOURED shadows, (3) brief,
// event-driven impact frames.
//
//  subject          ink (navy/violet, not black)                    rim
//  Aren             ~1.6 px at 1080p outer shell + inner armour edges  directional (key-light side), hard-edged
//  bosses           strong silhouette (×1.5, distance-aware)        on states (telegraph / stagger spike, low otherwise)
//  elites           thin outline (×1, distance-aware)               modest
//  regular foes     thinner than today (×0.7) so hordes never merge   none
//  props / terrain  existing edges unchanged                        none
//
//  shading  hero: 2-tone ramp, shadow band tinted indigo (lit band keeps the clean copper / teal); world: crisper ramp,
//           shadow colour from the map's own fill light pushed slightly cool (map-specific tint); hard spec on armour,
//           hair halo band
//  post     display-space grade: +saturation, cool shadows / warm highlights, gentle S-curve, soft corner vignette;
//           optional screentone in the deepest shadows (&tone=1)
//  impact   regular crit → tiny local speed-line accent (rate-limited); elite break → local ink silhouette flash + lines;
//           boss stagger (crit on the boss) / boss + guardian spawn / Surge transform → full-frame ink frame + lines.
//           Big ones go through FLASH.big() (src/render/a11y.ts); Reduced drops the inversion and dims, Minimal keeps faint
//           lines only; Glow Off (and Reduced) never floods the frame with paper white (dark "silhouette" frame instead).
import * as THREE from 'three/webgpu';
import {
  abs, atan, clamp, dot, float, floor, fract, hash, length, luminance, max, mix, normalView, positionViewDirection,
  min, renderOutput, saturation, screenCoordinate, screenSize, screenUV, smoothstep, step, uniform, vec2, vec3, vec4,
} from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import { FLASH } from './a11y';

/* eslint-disable @typescript-eslint/no-explicit-any */
type N = any;

const params = typeof location !== 'undefined' ? new URLSearchParams(location.search) : new URLSearchParams();
// owner 2026-11-02 ('I'll let you decide if any anime looks can be standardized'): the anime layer is now the default look
// (ink + rim hierarchy, coloured hero shadows, grade, small hit / elite accents); ?look=classic switches it all off.
// Full-frame ink frames (boss stagger / spawn, Surge) stay opt-in (&impact=full) — too bold for every run.
const LOOK_ON = params.get('look') !== 'classic';
const FULL_FRAME = params.get('impact') === 'full' || params.has('lookcheck');

/** ramps registered by the cel code: [texture, default data, anime data] */
const ramps: Array<[THREE.DataTexture, Uint8Array, Uint8Array]> = [];

const _v = new THREE.Vector3(), _c = new THREE.Color(), _q = new THREE.Vector3();
const COOL = new THREE.Color(0x8a96ff), WARM = new THREE.Color(0xffe6c4);
/** ink: deep navy / violet (P1: not pure black) */
export const ANIME_INK = 0x1a1842;

/** impact presets: level (priority), region radius (screen heights; ≥ 4 = full frame), line inner radius, duration (s),
 *  ink-frame time (s), whether it counts as a big flash */
const IMPACTS = {
  hit: { lvl: 1, r: 0.075, r0: 0.022, dur: 0.16, ink: 0, big: false },
  elite: { lvl: 2, r: 0.2, r0: 0.04, dur: 0.26, ink: 0.05, big: true },
  stagger: { lvl: 3, r: 10, r0: 0.24, dur: 0.3, ink: 0.05, big: true },
  spawn: { lvl: 4, r: 10, r0: 0.18, dur: 0.45, ink: 0.07, big: true },
  surge: { lvl: 4, r: 10, r0: 0.16, dur: 0.42, ink: 0.06, big: true },
} as const;
export type ImpactKind = keyof typeof IMPACTS;

export const ANIME = {
  /** nodes are built (flag on, or the lookcheck QA route which flips it at runtime) */
  enabled: LOOK_ON || params.has('lookcheck'),
  /** current look (true = anime) */
  on: LOOK_ON,
  /** screentone in deep shadows (optional; &tone=1) */
  tone: params.get('tone') === '1',
  /** 0..1 master blend for every node below */
  k: uniform(LOOK_ON ? 1 : 0),
  /** key light direction in view space (rim side, coloured shadow, specular) and colour */
  lightView: uniform(new THREE.Vector3(0, 1, 0)), lightCol: uniform(new THREE.Color(0xfff0d8)),
  /** camera-distance factor for foe ink (1 at the default 25 m camera); hero shell thickness (model units) */
  inkDist: uniform(1), heroInk: uniform(0.01),
  /** boss rim "state" spike (telegraph / stagger), decays */
  bossRim: uniform(0),
  /** impact frame uniforms */
  impK: uniform(0), impInv: uniform(0), impLines: uniform(0), impSeed: uniform(0), impSoft: uniform(0),
  impCenter: uniform(new THREE.Vector2(0.5, 0.5)), impR: uniform(10), impR0: uniform(0.2),
  /** Glow setting (view passes view.surge.glowOn) */
  glowOn: true,
  // impact state
  clock: 0, impT: 0, impDur: 0, impPeak: 0, impInkT: 0, impLvl: 0, impWorld: new THREE.Vector3(), impCount: 0,
  lastHit: -9, lastStagger: -9, bossRimV: 0,
  stats: { hit: 0, elite: 0, stagger: 0, spawn: 0, surge: 0 } as Record<ImpactKind, number>,
  /** QA: hold the impact at a fixed phase (seconds since trigger), null = animate */
  impHold: null as number | null,

  /** flip the look at runtime (only meaningful when `enabled`) */
  set(on: boolean): void {
    this.on = on; this.k.value = on ? 1 : 0;
    for (const [tex, def, an] of ramps) { (tex.image.data as Uint8Array).set(on ? an : def); tex.needsUpdate = true; }
  },

  /** called by toon.ts / heroGltf.ts for each shared ramp: 3 texels (shadow, mid, lit) as luminance bytes */
  registerRamp(tex: THREE.DataTexture, anime: [number, number, number]): void {
    if (!this.enabled) return;
    const def = new Uint8Array(tex.image.data as Uint8Array);
    const an = new Uint8Array(12); anime.forEach((v, i) => { an.set([v, v, v, 255], i * 4); });
    ramps.push([tex, def, an]);
    if (this.on) { (tex.image.data as Uint8Array).set(an); tex.needsUpdate = true; }
  },

  /** SimEvent hook (view.handleEvents): picks which moments get an impact frame. Render-only. */
  event(ev: SimEvent, sim: Sim): void {
    if (!this.enabled) return;
    switch (ev.t) {
      case 'hit': {
        if (!ev.crit) break;
        const b = sim.boss;
        if (b && b.alive && Math.hypot(ev.x - b.x, ev.z - b.z) < b.def.radius * b.scale + 1.2) {
          if (this.clock - this.lastStagger > 4) { this.lastStagger = this.clock; this.bossRimV = 1; this.impact('stagger', b.x, 2, b.z); }
        } else if (this.clock - this.lastHit > 0.5) { this.lastHit = this.clock; this.impact('hit', ev.x, ev.y + 0.6, ev.z); }
        break;
      }
      case 'kill': if (ev.elite && !ev.boss) this.impact('elite', ev.x, ev.y + 0.8, ev.z); break;
      case 'bossSpawn': case 'guardianSpawn': if (sim.boss) this.impact('spawn', sim.boss.x, 2.5, sim.boss.z); break;
      case 'transform': this.impact('surge', sim.player.x, 1.2, sim.player.z); break;
      case 'bossTele': this.bossRimV = 1; break;
    }
  },

  /** start an impact frame (a bigger one in progress is never cut short by a smaller one) */
  impact(kind: ImpactKind, x: number, y: number, z: number): void {
    if (!this.enabled || !this.on) return;
    if (IMPACTS[kind].r >= 4 && !FULL_FRAME) return; // full-frame ink frames are opt-in (&impact=full)
    const P = IMPACTS[kind];
    if (this.impDur > 0 && this.impLvl > P.lvl && this.impT < this.impDur * 0.6) return;
    if (FLASH.mode === 'minimal' && !P.big) return; // Minimal: no small accents at all
    const g = P.big ? FLASH.big() : FLASH.L.dim; // big ones count against the per-second flash budget
    this.impT = 0; this.impDur = P.dur; this.impPeak = g; this.impInkT = P.ink; this.impLvl = P.lvl;
    this.impWorld.set(x, y, z); this.impCount++; this.stats[kind]++;
    this.impSeed.value = (this.impCount * 17.13) % 97;
    this.impR.value = P.r; this.impR0.value = P.r0;
    this.impInv.value = FLASH.mode === 'off' && g >= 1 && P.lvl >= 4 && this.glowOn ? 1 : 0; // inversion: spawn / Surge only, flashes Off only
    this.impSoft.value = FLASH.mode !== 'off' || !this.glowOn ? 1 : 0;
  },

  /** per rendered frame (view.sync, after the daylight update): lights, ink width, rim state, impact envelope */
  frame(camera: THREE.PerspectiveCamera, hemi: THREE.HemisphereLight, sun: THREE.DirectionalLight, focus: THREE.Vector3, hero: THREE.Vector3, dt: number, glowOn: boolean): void {
    if (!this.enabled) return;
    this.clock += dt; this.glowOn = glowOn;
    const k = this.k.value as number;
    _v.copy(sun.position).sub(sun.target.position).normalize().transformDirection(camera.matrixWorldInverse);
    (this.lightView.value as THREE.Vector3).copy(_v);
    (this.lightCol.value as THREE.Color).copy(sun.color).lerp(WARM, 0.35);
    if (k > 0) {
      // map-specific coloured shadows: the map's own fill light, pushed a little cooler / more saturated; key a touch warmer
      hemi.color.lerp(_c.copy(hemi.color).multiply(COOL).multiplyScalar(1.2), 0.3 * k);
      hemi.groundColor.lerp(_c.copy(hemi.groundColor).multiply(COOL).multiplyScalar(1.1), 0.3 * k);
      sun.color.lerp(_c.copy(sun.color).multiply(WARM), 0.3 * k);
    }
    // ink: foe hulls follow the camera distance (≈ constant px); the hero shell targets ~1.6 px at 1080p
    this.inkDist.value = Math.min(1.6, Math.max(0.5, camera.position.distanceTo(focus) / 25));
    const t2 = 2 * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    const heroPx = Math.min(0.042, Math.max(0.008, (2.0 * camera.position.distanceTo(hero) * t2) / 1080 / 1.07));
    this.heroInk.value = 0.01 + k * (heroPx - 0.01);
    this.bossRimV = Math.max(0, this.bossRimV - dt * 1.4); this.bossRim.value = this.bossRimV;
    // impact envelope: a few frames of ink, then a quick fade of the speed lines
    if (this.impDur > 0) {
      if (this.impHold === null) this.impT += dt; else this.impT = this.impHold;
      const t = this.impT / this.impDur;
      if (t >= 1) { this.impDur = 0; this.impLvl = 0; this.impK.value = 0; this.impLines.value = 0; }
      else {
        const ink = this.impInkT > 0 ? (this.impT < this.impInkT ? 1 : Math.max(0, 1 - (this.impT - this.impInkT) / 0.08)) : 0;
        this.impK.value = ink * this.impPeak * (FLASH.mode === 'minimal' ? 0 : 1) * k;
        if (this.impT >= 0.045) this.impInv.value = 0; // inversion only on the first ~3 frames
        this.impLines.value = (1 - t) * Math.max(this.impPeak, FLASH.mode === 'minimal' ? 0.35 : 0) * k;
        _q.copy(this.impWorld).project(camera);
        const full = (this.impR.value as number) > 4;
        const cx = _q.x * 0.5 + 0.5, cy = 0.5 - _q.y * 0.5;
        (this.impCenter.value as THREE.Vector2).set(full ? THREE.MathUtils.clamp(cx, 0.2, 0.8) : cx, full ? THREE.MathUtils.clamp(cy, 0.2, 0.8) : cy);
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------------
// material nodes (zero / identity when k = 0)
// ---------------------------------------------------------------------------------------------------------------------

/** Hard-edged back-light rim: a thin band on the silhouette, brightest on the side facing the key light on screen. */
export function animeRim(gain: N): N {
  const facing = abs(dot(normalView, positionViewDirection));
  const edge = smoothstep(0.64, 0.7, float(1).sub(facing));
  const side = clamp(dot(normalView.xy, (ANIME.lightView as N).xy).mul(1.6).add(0.25), 0, 1);
  return (ANIME.lightCol as N).mul(edge.mul(side).mul(gain)).mul(ANIME.k);
}

/** Stepped Blinn highlight (armour): a hard white shape where N·H is near 1. */
export function animeSpec(gain: number): N {
  const h = (ANIME.lightView as N).add(positionViewDirection).normalize();
  return (ANIME.lightCol as N).mul(smoothstep(0.93, 0.945, dot(normalView, h)).mul(gain)).mul(ANIME.k);
}

/** Hair halo band: a hard highlight ring on the upper curve of the head as seen by the camera. */
export function animeHairBand(base: N, gain: number): N {
  const ny = normalView.y, facing = dot(normalView, positionViewDirection);
  const band = smoothstep(0.4, 0.44, ny).mul(float(1).sub(smoothstep(0.56, 0.6, ny))).mul(smoothstep(0.4, 0.5, facing));
  return mix(base, vec3(0.75, 0.82, 1), 0.3).mul(band.mul(gain)).mul(ANIME.k);
}

/** Hero albedo modifier: indigo shadow band (matches the hero ramp's shadow step, the lit band keeps the clean colour) and,
 *  for armour, an inner ink edge where the surface turns away from the camera (selective inner contours). */
export function animeHeroColor(base: N, innerInk: boolean): N {
  const ndl = dot(normalView, ANIME.lightView as N);
  const shade = float(1).sub(smoothstep(-0.36, -0.3, ndl)); // 1 in the ramp's shadow band
  // skin (bright, warm, high green) shades to a soft rose instead — indigo on a face reads as a bruise
  const skin = smoothstep(0.5, 0.62, base.g).mul(step(base.b.add(0.12), base.r));
  const tint = mix(vec3(0.62, 0.6, 1.08), vec3(0.95, 0.72, 0.8), skin);
  let c: N = base.mul(mix(vec3(1, 1, 1), tint, shade.mul(ANIME.k)));
  if (innerInk) {
    const edge = smoothstep(0.8, 0.86, float(1).sub(abs(dot(normalView, positionViewDirection))));
    c = mix(c, vec3(0.1, 0.09, 0.26), edge.mul(ANIME.k));
  }
  return c;
}

/** Foe outline thickness multiplier by class (1 when k = 0): regular thinner, elites as today, bosses heavier. */
export function animeInk(mult: number): N { return mix(float(1), (ANIME.inkDist as N).mul(mult), ANIME.k); }

// ---------------------------------------------------------------------------------------------------------------------
// post (display space: the pipeline's tone map + sRGB step runs first, inside renderOutput)
// ---------------------------------------------------------------------------------------------------------------------

/** Final output node: renderOutput(hdr) → grade → vignette → (screentone) → impact frame. Caller sets
 *  pipeline.outputColorTransform = false. With k = 0 and no impact it equals renderOutput(hdr). */
export function animeOutput(hdr: N): N {
  const base = renderOutput(hdr) as N;
  const kk = ANIME.k as N;
  // grade: vivid saturation, cool shadows / warm highlights, gentle S-curve
  // vibrance lifts the muted colours (skin, armour, rock) more than the already-vivid grass
  const c0 = base.rgb, mx = max(c0.r, max(c0.g, c0.b)), mn = min(c0.r, min(c0.g, c0.b));
  const sat = mx.sub(mn).div(mx.add(1e-4));
  const muted = float(1).sub(sat);
  let c: N = saturation(c0, float(1).add(kk.mul(float(0.03).add(muted.mul(muted).mul(0.3)))));
  const l = luminance(c);
  const split = mix(vec3(0.93, 0.95, 1.08), vec3(1.01, 1.0, 0.985), smoothstep(0.12, 0.7, l));
  c = c.mul(mix(vec3(1, 1, 1), split, kk));
  const cc = clamp(c, 0, 1);
  c = mix(c, cc.mul(cc).mul(float(3).sub(cc.mul(2))), kk.mul(0.12));
  // vignette: only the outer corners, tinted deep indigo (edge threats stay visible)
  const aspect = vec2(screenSize.x.div(screenSize.y), 1);
  const vig = smoothstep(0.6, 1.1, length(screenUV.sub(0.5).mul(aspect))).mul(kk);
  c = mix(c, c.mul(vec3(0.74, 0.72, 0.88)), vig.mul(0.5));
  if (ANIME.tone) {
    // screentone: 45° dot grid, 5 px cells, only where the image is already deep shadow
    const p = screenCoordinate.xy.div(5);
    const cell = fract(vec2(p.x.add(p.y), p.x.sub(p.y)).mul(0.7071)).sub(0.5);
    const shadow = float(1).sub(smoothstep(0.06, 0.16, luminance(c)));
    c = mix(c, c.mul(0.45), step(length(cell), shadow.mul(0.42)).mul(shadow).mul(kk));
  }
  // impact frame: hard ink threshold (optionally inverted) inside a region, + speed lines from the event's screen position
  const inkC = vec3(0.07, 0.06, 0.17);
  const paper = mix(vec3(1, 0.97, 0.92), c.mul(0.6), ANIME.impSoft); // soft (Reduced flash / Glow Off): no white flood
  let bw: N = mix(inkC, paper, step(0.38, luminance(c)));
  bw = mix(bw, vec3(1, 1, 1).sub(bw), ANIME.impInv);
  const dd = screenUV.sub(ANIME.impCenter as N).mul(aspect);
  const r = length(dd), R = ANIME.impR as N, R0 = ANIME.impR0 as N;
  const region = float(1).sub(smoothstep(R.mul(0.7), R, r));
  const ang = atan(dd.y, dd.x).mul(1 / (2 * Math.PI)).add(0.5);
  const hsh = hash(floor(ang.mul(150)).add(ANIME.impSeed as N));
  const thin = step(abs(fract(ang.mul(150)).sub(0.5)), hsh.mul(0.3));
  const start = R0.mul(hsh.add(1));
  const lines = step(0.68, hsh).mul(thin).mul(smoothstep(start, start.add(R0.mul(0.8)), r)).mul(region);
  const out = mix(c, bw, (ANIME.impK as N).mul(region));
  const lineCol = mix(vec3(1, 0.98, 0.94), inkC, step(0.5, luminance(out)));
  const final = mix(out, lineCol, lines.mul(max(ANIME.impLines as N, ANIME.impK as N)).mul(0.9));
  return vec4(final, base.a);
}
